import { store } from '../../Models/Store';
import { FerryTripEntity } from 'Models/Entities';
import { measurementType } from '../../Models/Enums';
import { isNotNullOrUndefined, isNullOrUndefined } from '../TypeGuards';
import { TicketsTabTrip } from '../../Services/Api/_HumanWritten/BookingWizardDataService';
import {
	filterByMeasurementRestrictions,
	MeasurementFilterContext,
	MeasurementRestrictionRules,
	pickAllowedMeasurement,
} from './MeasurementRestrictions';

export const getMinimumMeasurementTypeForFerryTrip = (
	ferryTrip?: FerryTripEntity | TicketsTabTrip | undefined,
	measurementOption: measurementType = 'LENGTH',
) => {
	if (isNullOrUndefined(ferryTrip)) {
		// This is a fallback for bulk bookings as there are multiple ferry trips associated to them.
		// Assumption: all ferry trips should have the same length/weight types associated to them.
		const measurements = store?.measurements;
		const storeMeasurements = measurements
			?.filter(x => x.measurementType === measurementOption)
			?.sort((m1, m2) => (m1.value > m2.value ? 1 : -1))[0];
		return storeMeasurements;
	}
	const filteredMeasurements = findAllMeasurementsForFerryTrip(measurementOption);
	// Sort smallest measurement value to largest to find the base measurement type/price
	return filteredMeasurements?.sort((m1, m2) => (m1.value > m2.value ? 1 : -1))[0];
};

export const getMinimumMeasurementFromStore = (measurementOption: measurementType = 'LENGTH') => {
	return store?.measurements
		?.filter(x => x.measurementType === measurementOption)
		?.sort((m1, m2) => (m1.value > m2.value ? 1 : -1))[0];
};

export const findAllMeasurementsForFerryTrip = (
	measurementOption?: measurementType,
	cargoType: 'vehicle' | 'trailer' | 'vehicle-and-trailer' = 'vehicle-and-trailer',
) => {
	let measurements = store?.measurements;
	if (measurementOption) {
		measurements = measurements?.filter(x => x.measurementType === measurementOption);
	}
	measurements = measurements?.filter(x => {
		if (cargoType === 'vehicle') {
			return x.showInVehicleDropdown;
		}
		if (cargoType === 'trailer') {
			return x.showInTrailerDropdown;
		}
		return x.showInVehicleDropdown || x.showInTrailerDropdown;
	});

	return measurements?.sort((x, y) => (x.value > y.value ? 1 : -1));
};

/**
 * The visibility rules the server sent down for a measurement, or undefined when it does not know
 * the id. Always read through the global list, so a measurement that reached a component from
 * somewhere else (a trip payload in check-in, say) still resolves its rules.
 */
export const getMeasurementRestrictionRules = (measurementId: string): MeasurementRestrictionRules | undefined =>
	store?.measurements?.find(x => x.id === measurementId);

/**
 * Applies the operator's per-measurement visibility rules to a list of measurements.
 *
 * **Display only.** Use this where options are rendered for someone to pick from, and nowhere else —
 * in particular not in the helpers that derive a weight band from an entered exact weight, or that
 * look up a minimum. A rule must never move a price or a capacity figure; it only decides what is
 * offered. See {@link filterByMeasurementRestrictions} for what the context's null-vs-undefined
 * distinction means.
 *
 * `keepIds` should carry whatever is currently selected, so the picker can always render its own
 * value even when a rule added later would have hidden it.
 */
export const filterMeasurementsForContext = <T extends { id: string }>(
	measurements: T[] | undefined | null,
	context: MeasurementFilterContext,
	keepIds: (string | null | undefined)[] = [],
): T[] => filterByMeasurementRestrictions(measurements, context, getMeasurementRestrictionRules, keepIds);

/**
 * Store-bound {@link pickAllowedMeasurement}: what a picker's selection should become once the
 * operator's visibility rules are applied, or undefined to leave it alone. Shared by the booking
 * wizard and check-in so the two flows cannot drift on what "allowed" means.
 */
export const resolveAllowedMeasurementId = <T extends { id: string; value?: number | null }>(
	currentId: string | undefined | null,
	candidates: T[] | undefined | null,
	context: MeasurementFilterContext,
	minimumValue: number = 0,
): string | undefined =>
	pickAllowedMeasurement(currentId, candidates, context, getMeasurementRestrictionRules, minimumValue);

/**
 * Sort comparator for measurement entities used in mixed-type pricing tables:
 * groups LENGTH before WEIGHT, then orders by value ascending within each group.
 */
export const compareMeasurementsByTypeThenValue = (
	a: { measurementType?: string | null; value?: number | null },
	b: { measurementType?: string | null; value?: number | null },
): number => {
	const typeOrder = (t: string | null | undefined) => (t === 'LENGTH' ? 0 : 1);
	const diff = typeOrder(a.measurementType) - typeOrder(b.measurementType);
	if (diff !== 0) return diff;
	return (a.value ?? 0) - (b.value ?? 0);
};

export const getMeasurementLabelFromId = (measurementId: string) => {
	const measurement = store?.measurements?.find(x => x.id === measurementId);
	if (isNullOrUndefined(measurement)) {
		return '';
	}
	return measurement.label;
};

export const getMeasurementValueFromId = (measurementId?: string, isTrailer: boolean = false) => {
	if (isNullOrUndefined(measurementId)) {
		return isTrailer ? 1 : 0;
	}
	const measurement = store?.measurements?.find(x => x.id === measurementId);
	if (isNullOrUndefined(measurement)) {
		return isTrailer ? 1 : 0;
	}
	return measurement.value;
};
